/**
 * Auth-event helpers for the F1.1 Sentry-based product-event pipeline.
 *
 * Card z838xWhL — OBS F1.2 Instrumentar 6 events del wizard + auth.
 *
 * Contract
 * --------
 * - `deriveAuthMethod(userSub)` clasifica el provider a partir del prefijo
 *   del ``sub`` del ID token: ``google-oauth2|...`` → 'google',
 *   ``apple|...`` → 'apple', ``auth0|...`` → 'email', anything else →
 *   'unknown'. La spec del F1.2 sólo pide ``method``, así que no leemos
 *   el ``connection`` del ``appState`` (Auth0 SDK no lo expone
 *   automáticamente y agregarlo requería tocar cada ``loginWithRedirect``).
 *
 * - `isFirstTimeUser()` / `markUserSeen()` — `localStorage`-backed flag
 *   ``inbest.user_seen``. La primera vez que un browser logea contra
 *   Auth0 el flag no existe → se emite ``user_signup``. En logins
 *   subsecuentes → ``user_login``. El flag NO se limpia por logout: es un
 *   marker de "este browser ya vio a un usuario alguna vez". Perdimos el
 *   heurístico si el user limpia storage o entra desde otro device, en
 *   cuyo caso el evento sale como ``user_signup`` — aceptable para métricas
 *   agregadas de funnel (Sentry deduplica downstream por user_id_hash).
 *
 * - `consumeAuthCallbackFlag()` — sessionStorage flag ``inbest.auth.callback_pending``.
 *   Escrito por ``Auth0Layout.onRedirectCallback`` cuando el flujo termina
 *   de vuelta en la SPA post-Auth0 redirect. El tracker (`AuthEventsTracker`)
 *   lee y consume el flag cuando ``isAuthenticated`` transita a ``true``,
 *   garantizando que ``user_signup``/``user_login`` disparen SOLO por un
 *   redirect real y no por silent-auth refresh (que también flipea
 *   ``isAuthenticated`` a ``true`` al arranque).
 */

const USER_SEEN_KEY = 'inbest.user_seen';
const AUTH_CALLBACK_FLAG_KEY = 'inbest.auth.callback_pending';

/**
 * Auth methods reportados. Deliberadamente restringido — cualquier
 * connection nueva cae a 'unknown' hasta que se agregue explícitamente
 * (fuerza un review consciente al pipeline).
 */
export type AuthMethod = 'google' | 'email' | 'apple' | 'unknown';

/**
 * Deriva el ``method`` del ``sub`` del ID token de Auth0.
 *
 * Prefijos observados en el tenant ``inbest-ai.us.auth0.com``:
 *   - ``google-oauth2|...``  → 'google'
 *   - ``auth0|...``          → 'email' (Username-Password-Authentication)
 *   - ``apple|...``          → 'apple' (Sign in with Apple)
 *   - todo lo demás          → 'unknown'
 *
 * `undefined` / string vacío se mapean a 'unknown' — nunca throw.
 */
export function deriveAuthMethod(userSub: string | undefined): AuthMethod {
  if (!userSub || typeof userSub !== 'string') return 'unknown';
  if (userSub.startsWith('google-')) return 'google';
  if (userSub.startsWith('apple|')) return 'apple';
  if (userSub.startsWith('auth0|')) return 'email';
  return 'unknown';
}

/**
 * `true` si el flag ``inbest.user_seen`` NO existe en localStorage — usado
 * como heurística "primer login de este browser". Falla-safe si storage
 * no está disponible: retorna ``false`` (asumimos returning, evitamos
 * flood de signup events falsos).
 */
export function isFirstTimeUser(): boolean {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    return window.localStorage.getItem(USER_SEEN_KEY) === null;
  } catch {
    return false;
  }
}

/**
 * Setea el flag ``inbest.user_seen``. Idempotente. Falla-safe si storage
 * no está disponible — no hace nada, y `isFirstTimeUser()` seguirá
 * retornando ``false`` (por la falla-safe descripta arriba) o ``true``
 * hasta que storage funcione (edge raro).
 */
export function markUserSeen(): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(USER_SEEN_KEY, '1');
  } catch {
    // Quota / private mode — no-op.
  }
}

/**
 * Escribe el flag ``inbest.auth.callback_pending`` en sessionStorage. Lo
 * llama ``Auth0Layout.onRedirectCallback`` para señalizar al tracker que
 * un redirect real acaba de completarse (vs silent-auth refresh que
 * también flipea ``isAuthenticated``).
 */
export function markAuthCallbackPending(): void {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    window.sessionStorage.setItem(AUTH_CALLBACK_FLAG_KEY, '1');
  } catch {
    // No-op — el tracker degrada a no-emit en este caso.
  }
}

/**
 * Lee-y-borra el flag. `true` significa "el user acaba de completar un
 * redirect Auth0". El tracker lo consume una sola vez por callback.
 */
export function consumeAuthCallbackFlag(): boolean {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return false;
    const val = window.sessionStorage.getItem(AUTH_CALLBACK_FLAG_KEY);
    if (val !== '1') return false;
    window.sessionStorage.removeItem(AUTH_CALLBACK_FLAG_KEY);
    return true;
  } catch {
    return false;
  }
}

/**
 * Test helper — resetea ambos flags. No exportado del index público.
 */
export function __resetAuthEventFlagsForTests(): void {
  try {
    window.localStorage.removeItem(USER_SEEN_KEY);
    window.sessionStorage.removeItem(AUTH_CALLBACK_FLAG_KEY);
  } catch {
    // ignore
  }
}
